import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';


const PerformanceStandards = () => {
    const { get, patch } = useApi();
    const { showToast, dispatch } = useAppState();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [localBands, setLocalBands] = useState([]);

    const fetchBands = useCallback(async () => {
        try {
            setLoading(true);
            const data = await get('/api/performance-bands/');
            // Sort by min_percent descending (EE1 to BE2)
            const sorted = [...data].sort((a, b) => b.min_percent - a.min_percent);
            setLocalBands(sorted);
            dispatch({ type: 'SET_PERFORMANCE_BANDS', payload: sorted });
        } catch (err) {
            console.error('Error fetching performance bands:', err);
            showToast('Failed to load performance standards.', 'error');
        } finally {
            setLoading(false);
        }
    }, [get, dispatch, showToast]);

    useEffect(() => {
        fetchBands();
    }, [fetchBands]);

    const handleUpdateBand = (id, field, value) => {
        setLocalBands(prev => prev.map(b => 
            b.id === id ? { ...b, [field]: parseFloat(value) || 0 } : b
        ));
    };

    const handleLabelUpdate = (id, value) => {
        setLocalBands(prev => prev.map(b => 
            b.id === id ? { ...b, label: value } : b
        ));
    };

    const saveChanges = async () => {
        setSaving(true);
        try {
            await Promise.all(localBands.map(band => 
                patch(`/api/performance-bands/${band.id}/`, band)
            ));
            showToast('Performance standards synchronized successfully.', 'success');
            await fetchBands();
        } catch (err) {
            console.error('Error saving bands:', err);
            showToast('Failed to save changes. Ensure ranges do not overlap.', 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="bg-white rounded-[2.5rem] p-20 text-center border border-slate-100 border-dashed animate-pulse">
            <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Calibrating Performance Matrix...</p>
        </div>
    );
    
    return (
        <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-50 p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-[#18216D] border border-indigo-100">
                        <i className="fas fa-chart-line text-xl"></i>
                    </div>
                    <div>
                        <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-3">
                            Performance Standards Registry
                        </h2>
                        <p className="text-[10px] font-bold text-[#18216D] uppercase tracking-tighter mt-1 italic">Configure Grading Tiers, Percentages & MS Ranges</p>
                    </div>
                </div>

                <button
                    onClick={saveChanges}
                    disabled={saving}
                    className="px-8 py-3 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:scale-[1.02] transition-all active:scale-95 shadow-xl shadow-indigo-900/10 disabled:opacity-50 flex items-center gap-3"
                >
                    {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-cloud-arrow-up"></i>}
                    {saving ? 'Synchronizing...' : 'Deploy Changes'}
                </button>
            </div>

            {/* Standards Table */}
            <div className="bg-slate-50/50 rounded-[2rem] border border-slate-100 overflow-hidden mb-10">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-slate-100">
                                <th className="py-5 text-left text-[9px] font-black text-[#18216D] uppercase tracking-widest pl-8">Band</th>
                                <th className="py-5 text-left text-[9px] font-black text-[#18216D] uppercase tracking-widest">Label / Description</th>
                                <th className="py-5 text-center text-[9px] font-black text-[#18216D] uppercase tracking-widest">Min %</th>
                                <th className="py-5 text-center text-[9px] font-black text-[#18216D] uppercase tracking-widest">Max %</th>
                                <th className="py-5 text-center text-[9px] font-black text-[#18216D] uppercase tracking-widest">Min MS</th>
                                <th className="py-5 text-center text-[9px] font-black text-[#18216D] uppercase tracking-widest">Max MS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {localBands.map((band) => (
                                <tr key={band.id} className="group hover:bg-white transition-colors">
                                    <td className="py-5 pl-8">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-black text-[10px] ${
                                            band.band.startsWith('EE') ? 'bg-amber-50 text-amber-600' :
                                            band.band.startsWith('ME') ? 'bg-emerald-50 text-emerald-600' :
                                            band.band.startsWith('AE') ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-400'
                                        }`}>
                                            {band.band}
                                        </div>
                                    </td>
                                    <td className="py-5">
                                        <input
                                            type="text"
                                            value={band.label}
                                            onChange={(e) => handleLabelUpdate(band.id, e.target.value)}
                                            className="w-full bg-transparent border-none text-[11px] font-bold text-[#18216D] uppercase focus:ring-0 placeholder:text-slate-200"
                                            placeholder="Enter Label"
                                        />
                                    </td>
                                    <td className="py-5 text-center">
                                        <input
                                            type="number"
                                            value={band.min_percent}
                                            onChange={(e) => handleUpdateBand(band.id, 'min_percent', e.target.value)}
                                            className="w-14 h-9 bg-white border border-slate-100 rounded-lg text-center text-[10px] font-black text-[#18216D] focus:ring-2 focus:ring-indigo-100"
                                        />
                                    </td>
                                    <td className="py-5 text-center">
                                        <input
                                            type="number"
                                            value={band.max_percent}
                                            onChange={(e) => handleUpdateBand(band.id, 'max_percent', e.target.value)}
                                            className="w-14 h-9 bg-white border border-slate-100 rounded-lg text-center text-[10px] font-black text-[#18216D] focus:ring-2 focus:ring-indigo-100"
                                        />
                                    </td>
                                    <td className="py-5 text-center">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={band.min_ms}
                                            onChange={(e) => handleUpdateBand(band.id, 'min_ms', e.target.value)}
                                            className="w-14 h-9 bg-indigo-50/50 border border-indigo-100/50 rounded-lg text-center text-[10px] font-black text-indigo-600 focus:ring-2 focus:ring-indigo-100"
                                        />
                                    </td>
                                    <td className="py-5 text-center">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={band.max_ms}
                                            onChange={(e) => handleUpdateBand(band.id, 'max_ms', e.target.value)}
                                            className="w-14 h-9 bg-indigo-50/50 border border-indigo-100/50 rounded-lg text-center text-[10px] font-black text-indigo-600 focus:ring-2 focus:ring-indigo-100"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="p-6 bg-[#18216D]/5 rounded-[2rem] border border-[#18216D]/10 flex items-start gap-5">
                <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-[#18216D] shadow-sm border border-slate-100">
                    <i className="fas fa-info-circle"></i>
                </div>
                <div>
                    <h4 className="text-[9px] font-black text-[#18216D] uppercase tracking-widest mb-0.5">Architectural Integrity</h4>
                    <p className="text-[10px] font-bold text-[#18216D]/60 leading-relaxed uppercase">
                        Mean Scores (MS) are calculated using high-fidelity interpolation based on the ranges above. 
                        Changes here will instantly re-scale performance across all Student and Class dashboards. 
                        Ensure that percentage ranges are contiguous and non-overlapping to maintain data integrity.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PerformanceStandards;
